import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Envelope, Phone, Buildings, ArrowsClockwise, User, ClipboardText,
  ArrowRight, CheckCircle, Archive, ChatCircle, MagnifyingGlass, Info,
} from '@phosphor-icons/react';

import { confirm } from '../../components/ConfirmDialog';
import { notify } from '../../lib/notify';
import { InlineSkeleton } from '../../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 5-stage funnel per user's request:
//   new → contacted → qualified → converted → archived
const STATUSES = [
  { id: 'all',       label: 'All',        color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { id: 'new',       label: 'New',        color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { id: 'contacted', label: 'Contacted',  color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { id: 'qualified', label: 'Qualified',  color: 'bg-violet-100 text-violet-700 border-violet-200' },
  { id: 'converted', label: 'Converted',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { id: 'archived',  label: 'Archived',   color: 'bg-slate-200 text-slate-600 border-slate-300' },
];

const NEXT_STATUS = { new: 'contacted', contacted: 'qualified', qualified: 'converted' };

/**
 * Super-admin marketing-leads inbox. Left column = tabs+search+list;
 * right column = detail pane with note-trail + status advance.
 *
 * Non-goals for this iteration:
 *   * assignee dropdown (there's no CRM sales rep concept at the platform
 *     level yet — just admin@vooxy.com). Kept the DB field so it can be
 *     wired later without a migration.
 *   * inbound reply threading — the contact form isn't email-round-tripped
 *     so a note timeline is enough context.
 */
export default function MarketingLeadsInbox() {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const r = await axios.get(`${API}/marketing/leads`, { params });
      const list = r.data.leads || [];
      setLeads(list);
      setCounts(r.data.counts_by_status || {});
      // If current selection dropped out of the filter, clear it.
      if (selectedId && !list.some((l) => l.id === selectedId)) {
        setSelectedId(list[0]?.id || null);
      } else if (!selectedId && list.length) {
        setSelectedId(list[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const filtered = useMemo(() => {
    if (!search.trim()) return leads;
    const s = search.trim().toLowerCase();
    return leads.filter((l) =>
      (l.name || '').toLowerCase().includes(s) ||
      (l.company || '').toLowerCase().includes(s) ||
      (l.email || '').toLowerCase().includes(s) ||
      (l.interested_plan || '').toLowerCase().includes(s)
    );
  }, [leads, search]);

  const selected = filtered.find((l) => l.id === selectedId) || leads.find((l) => l.id === selectedId);

  const patchLead = async (updates) => {
    if (!selected) return;
    setSavingId(selected.id);
    try {
      const r = await axios.patch(`${API}/admin/marketing-leads/${selected.id}`, updates);
      // Replace in-place; refetch counts so tab badges stay accurate.
      setLeads((prev) => prev.map((l) => l.id === selected.id ? r.data : l));
      if (updates.note) setNoteDraft('');
      // Refresh counts (cheap — just a small aggregation)
      const cr = await axios.get(`${API}/marketing/leads`);
      setCounts(cr.data.counts_by_status || {});
    } catch (e) {
      notify(e.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const advance = () => {
    if (!selected) return;
    const nxt = NEXT_STATUS[selected.status];
    if (!nxt) return;
    patchLead({ status: nxt });
  };

  const archive = async () => {
    if (!selected) return;
    if (!await confirm({ message: `Archive lead from ${selected.company}?` })) return;
    patchLead({ status: 'archived' });
  };

  const addNote = () => {
    if (!noteDraft.trim()) return;
    patchLead({ note: noteDraft.trim() });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 h-[calc(100vh-220px)] min-h-[500px]" data-testid="marketing-leads-inbox">
      {/* ─────────────────── Left: list ─────────────────── */}
      <div className="flex flex-col medical-card overflow-hidden">
        {/* Status tabs */}
        <div className="p-3 border-b border-[#D5E8E4] flex gap-1 overflow-x-auto" data-testid="leads-status-tabs">
          {STATUSES.map((s) => {
            const active = s.id === statusFilter;
            const count = s.id === 'all'
              ? Object.values(counts).reduce((a, b) => a + b, 0)
              : counts[s.id] || 0;
            return (
              <button
                key={s.id}
                onClick={() => setStatusFilter(s.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                  active ? s.color : 'bg-white text-[#6D8E87] border-[#D5E8E4] hover:border-[#00a69c]'
                }`}
                data-testid={`leads-tab-${s.id}`}
              >
                {s.label}
                <span className={`text-[9px] font-bold px-1 rounded ${active ? 'bg-white/70' : 'bg-[#F4F9F7]'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search + reload */}
        <div className="p-3 border-b border-[#D5E8E4] flex gap-2">
          <div className="flex-1 relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6D8E87]" />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, email…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-[#D5E8E4] rounded-lg focus:outline-none focus:border-[#00a69c]"
              data-testid="leads-search"
            />
          </div>
          <button
            onClick={load}
            className="p-1.5 rounded-lg border border-[#D5E8E4] text-[#3B5A54] hover:bg-[#F4F9F7] hover:border-[#00a69c] hover:text-[#00a69c]"
            title="Reload"
            data-testid="leads-reload"
          >
            <ArrowsClockwise size={14} />
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto" data-testid="leads-list">
          {loading ? (
            <InlineSkeleton />
          ) : error ? (
            <div className="p-4 text-sm text-red-600">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#6D8E87]">
              {search ? 'No leads match your search.' : 'No leads in this bucket yet.'}
            </div>
          ) : (
            filtered.map((l) => (
              <LeadRow
                key={l.id}
                lead={l}
                selected={l.id === selectedId}
                onClick={() => setSelectedId(l.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* ─────────────────── Right: detail ─────────────────── */}
      <div className="medical-card overflow-hidden flex flex-col">
        {!selected ? (
          <div className="flex-1 grid place-items-center text-sm text-[#6D8E87]">
            <div className="text-center max-w-[280px]">
              <Envelope size={32} className="mx-auto text-[#D5E8E4] mb-3" weight="duotone" />
              Select a lead from the list to see full details.
            </div>
          </div>
        ) : (
          <>
            <div className="p-5 border-b border-[#D5E8E4]">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold mb-1 flex items-center gap-2">
                    <span>Lead · {new Date(selected.created_at).toLocaleString()}</span>
                    <StatusPill status={selected.status || 'new'} />
                  </div>
                  <h3 className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }} data-testid="lead-detail-name">
                    {selected.name}
                  </h3>
                  <div className="text-sm text-[#3B5A54] mt-0.5" data-testid="lead-detail-company">
                    {selected.company}{selected.employees ? ` · ${selected.employees}` : ''}
                  </div>
                </div>
              </div>

              {/* Contact strip */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-[#3B5A54] hover:text-[#00a69c]" data-testid="lead-detail-email">
                  <Envelope size={14} /> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-[#3B5A54] hover:text-[#00a69c]" data-testid="lead-detail-phone">
                    <Phone size={14} /> {selected.phone}
                  </a>
                )}
                {selected.interested_plan && (
                  <div className="flex items-center gap-2 text-[#3B5A54]" data-testid="lead-detail-plan">
                    <Buildings size={14} /> Interested in <b className="uppercase text-[#00a69c]">{selected.interested_plan}</b>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5" data-testid="lead-detail-body">
              {/* Original message */}
              {selected.message && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold mb-2">Their message</div>
                  <div className="text-sm text-[#052A23] whitespace-pre-wrap medical-card p-4 border-l-4 border-[#00a69c]" data-testid="lead-detail-message">
                    {selected.message}
                  </div>
                </div>
              )}

              {/* Note trail */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold mb-2 flex items-center gap-2">
                  <ChatCircle size={12} /> Internal notes
                </div>
                <div className="space-y-2" data-testid="lead-detail-notes">
                  {(selected.notes || []).length === 0 && (
                    <div className="text-xs text-[#6D8E87] italic">No notes yet — add the first below.</div>
                  )}
                  {(selected.notes || []).map((n, i) => (
                    <div key={i} className="medical-card p-3" data-testid={`lead-note-${i}`}>
                      <div className="text-xs text-[#3B5A54] whitespace-pre-wrap">{n.text}</div>
                      <div className="mt-1 text-[10px] text-[#6D8E87] flex items-center gap-1">
                        <User size={10} /> {n.author_name || n.author_email} · {new Date(n.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex gap-2">
                  <textarea
                    rows={2}
                    placeholder="Add a note… (visible only to super-admins)"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }}
                    className="flex-1 px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm resize-none focus:outline-none focus:border-[#00a69c]"
                    data-testid="lead-note-input"
                  />
                  <button
                    onClick={addNote}
                    disabled={!noteDraft.trim() || savingId === selected.id}
                    className="px-3 py-2 bg-[#00a69c] hover:bg-[#008b83] text-white text-xs font-semibold rounded-lg disabled:bg-[#00a69c]/40 disabled:cursor-not-allowed"
                    data-testid="lead-note-add"
                  >
                    Add note
                  </button>
                </div>
                <div className="mt-1 text-[10px] text-[#6D8E87] flex items-center gap-1">
                  <Info size={10} /> Cmd/Ctrl+Enter to add quickly.
                </div>
              </div>

              {/* Meta */}
              <div className="text-[11px] text-[#6D8E87] pt-2 border-t border-[#D5E8E4] space-y-0.5">
                <div>IP: <code>{selected.ip || '—'}</code></div>
                <div className="truncate">User-agent: <code>{selected.user_agent || '—'}</code></div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-[#D5E8E4] bg-[#F4F9F7] flex gap-2">
              <button
                onClick={advance}
                disabled={!NEXT_STATUS[selected.status] || savingId === selected.id}
                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#00a69c] hover:bg-[#008b83] text-white text-sm font-bold rounded-lg disabled:bg-[#00a69c]/40 disabled:cursor-not-allowed"
                data-testid="lead-advance-btn"
              >
                {NEXT_STATUS[selected.status]
                  ? <>Move to <b className="uppercase">{NEXT_STATUS[selected.status]}</b><ArrowRight size={14} weight="bold" /></>
                  : selected.status === 'converted' ? <><CheckCircle size={14} weight="fill" /> Converted</>
                  : <>End of funnel</>}
              </button>
              {selected.status !== 'archived' && (
                <button
                  onClick={archive}
                  disabled={savingId === selected.id}
                  className="inline-flex items-center gap-2 px-3 py-2 border border-[#D5E8E4] text-[#3B5A54] hover:bg-white hover:border-red-300 hover:text-red-600 text-sm font-medium rounded-lg"
                  data-testid="lead-archive-btn"
                >
                  <Archive size={14} /> Archive
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const LeadRow = ({ lead, selected, onClick }) => {
  const initial = (lead.name || lead.company || '?').charAt(0).toUpperCase();
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 border-b border-[#D5E8E4] hover:bg-[#F4F9F7] transition flex gap-3 ${selected ? 'bg-[#E2F3F0]' : ''}`}
      data-testid={`lead-row-${lead.id}`}
    >
      <div className={`w-9 h-9 rounded-full grid place-items-center font-bold text-sm shrink-0 ${selected ? 'bg-[#00a69c] text-white' : 'bg-[#F4F9F7] text-[#00a69c]'}`}>
        {initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-[#052A23] truncate">{lead.name}</span>
          <StatusPill status={lead.status || 'new'} small />
        </div>
        <div className="text-xs text-[#6D8E87] truncate">{lead.company}</div>
        <div className="text-[10px] text-[#6D8E87] mt-0.5 flex items-center gap-2">
          <span>{lead.email}</span>
          {lead.notes?.length > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[#00a69c] font-semibold">
              <ClipboardText size={10} /> {lead.notes.length}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

const StatusPill = ({ status, small }) => {
  const cfg = STATUSES.find((s) => s.id === status) || STATUSES[1];
  return (
    <span
      className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide border ${cfg.color} ${small ? 'text-[9px]' : 'text-[10px]'}`}
      data-testid={`status-pill-${status}`}
    >
      {cfg.label}
    </span>
  );
};
